import { useState } from 'react';
import type { RoutingBreakerRecord } from '@nadoutong/contracts';
import {
  useResetRoutingBreaker,
  useRoutingBreakers,
} from '../api/useRoutingBreakers.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Drawer,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import { RoutingBreakerSettingsPanel } from './RoutingBreakerSettingsPanel.tsx';
import { RoutingBreakerTripDialog } from './RoutingBreakerTripDialog.tsx';
import type { BreakerTripTarget } from './RoutingBreakerTripDialog.tsx';
import {
  breakerRemainingSeconds,
  formatBreakerIdentity,
} from './tenantRoutingShared.ts';

/**
 * Routing-breaker panel (epic #886 PR-6): the health-strip breaker count
 * opens this drawer. Lists every open / half-open / force-opened target
 * identity with labels, reason, and per-row force-close (reset) /
 * force-open (trip) actions. Data polls every ~15s via
 * {@link useRoutingBreakers}.
 */

/**
 * Three-state breaker chip, shared with the config-sheet pool rows:
 * open (danger, cooldown remaining), half-open (probing), forced
 * (manually removed until a deadline).
 */
export function BreakerStateChip({ record }: { readonly record: RoutingBreakerRecord }): JSX.Element {
  const t = useT();
  if (record.state === 'half_open') {
    return (
      <Chip tone="warn" testId="routing-breaker-chip-half-open">
        {t('tenant_routing.breaker.chip_half_open')}
      </Chip>
    );
  }
  if (record.state === 'forced') {
    return (
      <Chip tone="warn" testId="routing-breaker-chip-forced">
        {t('tenant_routing.breaker.chip_forced', {
          until: record.forced_until === null ? '—' : formatLocalTimestamp(record.forced_until),
        })}
      </Chip>
    );
  }
  return (
    <Chip tone="bad" pulse testId="routing-breaker-chip-open">
      {t('tenant_routing.breaker.chip_open', {
        seconds: String(breakerRemainingSeconds(record.cooldown_remaining_ms)),
      })}
    </Chip>
  );
}

/** Identity + display label for a breaker record, in trip-dialog shape. */
function breakerTripTargetForRecord(record: RoutingBreakerRecord): BreakerTripTarget {
  return {
    identity: {
      upstream_id: record.upstream_id,
      account_id: record.account_id,
      key_bucket_id: record.key_bucket_id,
      upstream_channel_id: record.upstream_channel_id,
    },
    label: formatBreakerIdentity(record),
  };
}

export function RoutingBreakersDrawer({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const breakers = useRoutingBreakers();
  const resetBreaker = useResetRoutingBreaker();
  const [resetDraft, setResetDraft] = useState<RoutingBreakerRecord | null>(null);
  const [tripTarget, setTripTarget] = useState<BreakerTripTarget | null>(null);
  const [view, setView] = useState<'list' | 'settings'>('list');

  const enabled = breakers.data?.enabled ?? true;
  const rows = breakers.data?.breakers ?? [];

  function handleResetConfirm(): void {
    if (resetDraft === null) return;
    resetBreaker.mutate(
      {
        upstream_id: resetDraft.upstream_id,
        account_id: resetDraft.account_id,
        key_bucket_id: resetDraft.key_bucket_id,
        upstream_channel_id: resetDraft.upstream_channel_id,
      },
      {
        onSuccess: () => {
          setResetDraft(null);
          toast.success(t('tenant_routing.breaker.toast.reset'));
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setView('list');
            onClose();
          }
        }}
        width="lg"
        title={
          view === 'settings'
            ? t('tenant_routing.breaker.settings.title')
            : t('tenant_routing.breaker.drawer_title')
        }
        description={t('tenant_routing.breaker.drawer_hint')}
        closeLabel={t('common.close')}
        testId="routing-breakers-drawer"
      >
        <div className="mb-3 flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setView((prev) => (prev === 'list' ? 'settings' : 'list'))}
            data-testid="routing-breakers-settings-toggle"
          >
            {view === 'list'
              ? t('tenant_routing.breaker.settings.open')
              : t('tenant_routing.breaker.settings.back')}
          </Button>
        </div>
        {view === 'settings' ? (
          <RoutingBreakerSettingsPanel />
        ) : breakers.isPending ? (
          <p className="m-0 text-body text-paper-low">{t('tenant_routing.loading')}</p>
        ) : !enabled ? (
          <p
            className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
            data-testid="routing-breakers-disabled"
          >
            {t('tenant_routing.breaker.disabled')}
          </p>
        ) : rows.length === 0 ? (
          <p
            className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
            data-testid="routing-breakers-empty"
          >
            {t('tenant_routing.breaker.empty')}
          </p>
        ) : (
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>{t('tenant_routing.breaker.table.identity')}</Th>
                <Th>{t('tenant_routing.breaker.table.state')}</Th>
                <Th>{t('tenant_routing.breaker.table.reason')}</Th>
                <Th>{t('tenant_routing.breaker.table.opened')}</Th>
                <Th className="text-right">{t('tenant_routing.breaker.table.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((record) => {
                const rowKey = [
                  record.upstream_id,
                  record.account_id ?? '',
                  record.key_bucket_id ?? '',
                  record.upstream_channel_id ?? '',
                ].join(':');
                return (
                  <Tr key={rowKey} interactive={false} data-testid={`routing-breaker-row-${record.upstream_id}`}>
                    <Td>
                      <span className="block truncate text-paper" title={formatBreakerIdentity(record)}>
                        {formatBreakerIdentity(record)}
                      </span>
                      <span className="mt-0.5 block truncate font-mono text-micro text-paper-low">
                        {record.upstream_id}
                      </span>
                    </Td>
                    <Td>
                      <BreakerStateChip record={record} />
                    </Td>
                    <Td mono>
                      {record.last_error_class ?? t('tenant_routing.breaker.reason_none')}
                    </Td>
                    <Td mono className="whitespace-nowrap">
                      {record.opened_at === null
                        ? t('tenant_routing.breaker.reason_none')
                        : formatLocalTimestamp(record.opened_at)}
                    </Td>
                    <Td className="text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <Button
                          size="sm"
                          disabled={resetBreaker.isPending}
                          onClick={() => setResetDraft(record)}
                          data-testid={`routing-breaker-reset-${record.upstream_id}`}
                        >
                          {t('tenant_routing.breaker.reset')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setTripTarget(breakerTripTargetForRecord(record))}
                          data-testid={`routing-breaker-trip-${record.upstream_id}`}
                        >
                          {t('tenant_routing.breaker.trip')}
                        </Button>
                      </span>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </Drawer>

      <ConfirmDialog
        open={resetDraft !== null}
        onOpenChange={(next) => {
          if (!next) setResetDraft(null);
        }}
        title={t('tenant_routing.breaker.reset_confirm_title')}
        body={resetDraft === null ? undefined : (
          <span className="flex flex-col gap-2">
            <span className="break-all text-paper">{formatBreakerIdentity(resetDraft)}</span>
            <span className="text-paper-low">{t('tenant_routing.breaker.reset_confirm_body')}</span>
          </span>
        )}
        confirmLabel={t('tenant_routing.breaker.reset')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={handleResetConfirm}
        loading={resetBreaker.isPending}
        testId="routing-breaker-reset-confirm"
      />

      <RoutingBreakerTripDialog target={tripTarget} onClose={() => setTripTarget(null)} />
    </>
  );
}
